import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { stateApi } from "@/lib/api";
import { toast } from "sonner";

const StateContext = createContext(null);

export const FinancialStateProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const saveTimer = useRef(null);
  const pendingSummary = useRef("Dashboard updated");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await stateApi.get();
      let st = data.state;
      // Normalize legacy debt balances (previously stored as negative) to a positive "amount owed"
      // so payments/transfers consistently reduce what you owe.
      if ((st.accounts || []).some((a) => a.type === "debt" && Number(a.balance) < 0)) {
        st = {
          ...st,
          accounts: st.accounts.map((a) => (a.type === "debt"
            ? { ...a, balance: Math.abs(Number(a.balance || 0)) }
            : a)),
        };
        stateApi.save(st, "Normalized debt balances").catch(() => {});
      }
      // Recurring bills: reset "paid" flags at the start of each new month
      const currentMonth = new Date().toISOString().slice(0, 7);
      if ((st.bills || []).length && st.settings?.billingMonth && st.settings.billingMonth !== currentMonth) {
        st = {
          ...st,
          bills: st.bills.map((b) => ({ ...b, paid: false })),
          settings: { ...(st.settings || {}), billingMonth: currentMonth },
        };
        stateApi.save(st, "Reset bills for new month").catch(() => {});
        toast.info("New month — recurring bills reset to unpaid");
      } else if (!st.settings?.billingMonth) {
        // stamp the current month without wiping existing paid flags
        st = { ...st, settings: { ...(st.settings || {}), billingMonth: currentMonth } };
      }
      setState(st);
    } catch (e) {
      toast.error("Could not load your data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doSave = useCallback(async (next, summary) => {
    setSaveStatus("saving");
    try {
      await stateApi.save(next, summary);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 1500);
    } catch (e) {
      setSaveStatus("error");
      toast.error("Failed to save changes");
    }
  }, []);

  // Update state and schedule a debounced save
  const update = useCallback((updater, summary = "Dashboard updated") => {
    setState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      pendingSummary.current = summary;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => doSave(next, pendingSummary.current), 700);
      return next;
    });
  }, [doSave]);

  const saveNow = useCallback((summary) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (state) doSave(state, summary || pendingSummary.current);
  }, [state, doSave]);

  return (
    <StateContext.Provider value={{ state, setState, update, saveNow, loading, saveStatus, reload: load }}>
      {children}
    </StateContext.Provider>
  );
};

export const useFinancialState = () => useContext(StateContext);
